import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ZiweiChart, getPalaceInterpretation, getOverallInterpretation } from '@/lib/ziwei-calculator';
import { BookOpen, Star, TrendingUp } from 'lucide-react';

interface InterpretationPanelProps {
  chart: ZiweiChart;
}

const InterpretationPanel: React.FC<InterpretationPanelProps> = ({ chart }) => {
  const overallInterpretation = getOverallInterpretation(chart);
  
  // 重要宮位
  const importantPalaces = [
    chart.palaces.find(p => p.name === '命宮'),
    chart.palaces.find(p => p.name === '財帛宮'),
    chart.palaces.find(p => p.name === '官祿宮'),
    chart.palaces.find(p => p.name === '夫妻宮'),
    chart.palaces.find(p => p.name === '福德宮'),
    chart.palaces.find(p => p.name === '遷移宮')
  ].filter(Boolean);

  // 獲取主要星曜統計
  const getStarStatistics = () => {
    const mainStars = chart.mainStars.filter(star => star.type === 'main');
    const brightStars = mainStars.filter(star => ['廟', '旺', '得'].includes(star.brightness));
    const weakStars = mainStars.filter(star => ['不', '陷'].includes(star.brightness));
    
    return {
      total: mainStars.length,
      bright: brightStars.length,
      weak: weakStars.length,
      brightStars,
      weakStars
    };
  };

  const starStats = getStarStatistics();

  return (
    <div className="space-y-6">
      {/* 整體分析 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            整體命格分析
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            <div className="whitespace-pre-line text-sm leading-relaxed">
              {overallInterpretation}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* 星曜統計 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5" />
            星曜分佈統計
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{starStats.total}</div>
              <div className="text-sm text-muted-foreground">主星總數</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{starStats.bright}</div>
              <div className="text-sm text-muted-foreground">廟旺得地</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{starStats.weak}</div>
              <div className="text-sm text-muted-foreground">不得地陷</div>
            </div>
          </div>
          
          {starStats.brightStars.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2">廟旺星曜：</h4>
              <div className="flex flex-wrap gap-1">
                {starStats.brightStars.map((star, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {star.name}({star.brightness})
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {starStats.weakStars.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2">失陷星曜：</h4>
              <div className="flex flex-wrap gap-1">
                {starStats.weakStars.map((star, index) => (
                  <Badge key={index} variant="destructive" className="text-xs">
                    {star.name}({star.brightness})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 重要宮位解讀 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            重要宮位解讀
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-80">
            <div className="space-y-4">
              {importantPalaces.map((palace, index) => (
                <div key={index}>
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold">{palace?.name}</h4>
                    {palace?.position === chart.bodyPalace && (
                      <Badge variant="destructive" className="text-xs">身宮</Badge>
                    )}
                  </div>
                  
                  {palace?.stars.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {palace.stars.map((star, starIndex) => (
                        <Badge 
                          key={starIndex} 
                          variant="outline" 
                          className="text-xs"
                        >
                          {star.name}({star.brightness})
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {palace ? getPalaceInterpretation(palace) : ''}
                  </p>
                  
                  {index < importantPalaces.length - 1 && (
                    <Separator className="mt-4" />
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* 大限資訊 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">當前大限</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="text-lg font-semibold text-purple-600">
              {chart.lifeStage}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              每個大限為十年，影響該階段的運勢走向
            </p>
          </div>
        </CardContent>
      </Card>

      {/* 注意事項 */}
      {!chart.birthInfo.hasTime && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-sm text-yellow-800">重要提醒</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-yellow-700">
              此排盤因缺少確切出生時間，使用午時（12:00）計算。
              命宮、身宮位置及部分星曜可能與實際情況有差異。
              建議提供準確出生時間以獲得更精確的分析結果。
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InterpretationPanel;